import React, { InputHTMLAttributes } from 'react';

import { queryLocationSuggestions } from 'src/common/LocationTypeahead/queryLocationSuggestions';
import { Combobox } from 'src/common/primitives/ComboBox';

import { useAuth } from '../../features/auth/useAuth';
import { LatLong } from '../external/aws/suggestPois';
import { SelectOption } from '../primitives/Select';
import { mapSuggestionResultsToSelectOptions } from './mapSuggestionResultsToSelectOptions';

export type LocationTypeaheadProps = {
  onChange?: (value: SelectOption) => void;
  value?: any;
  placeholder?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  error?: string;
  disabled?: boolean;
  additionalResultsQueryFn?: (
    query: string,
    options: SelectOption[]
  ) => Promise<SelectOption[]>;
  renderOption?: (
    option: SelectOption,
    selected: boolean,
    active: boolean
  ) => React.ReactNode;
};

export const LocationTypeahead = React.forwardRef(function _LocationTypeahead(
  {
    onChange,
    value,
    placeholder = 'Start typing and select an address',
    inputProps,
    error,
    disabled,
    additionalResultsQueryFn = async (query, options) => options,
    renderOption,
  }: LocationTypeaheadProps,
  forwardedRef: React.Ref<HTMLInputElement>
) {
  const auth = useAuth();
  const bias: LatLong | undefined = auth.currentOrganizationLocation?.latLong;

  return (
    <Combobox
      disabled={disabled}
      dataTestId={inputProps?.id}
      keepValue={true}
      onChange={onChange}
      options={value ? [{ value: value.value, label: value.label }] : []}
      value={value}
      placeholder={placeholder}
      ref={forwardedRef}
      queryFn={async (query) => {
        let mappedSuggestions: SelectOption[] = [];
        if (query) {
          const suggestions = await Promise.resolve(
            queryLocationSuggestions(query, bias) || []
          );
          mappedSuggestions = mapSuggestionResultsToSelectOptions(suggestions);
        }

        return additionalResultsQueryFn(query, mappedSuggestions);
      }}
      renderOption={renderOption}
      error={error}
    />
  );
});
